import React, { useCallback, useEffect, useState } from "react";
import {
  getRushReminderRecipients,
  addRushReminderRecipient,
  deleteRushReminderRecipient,
  runRushReminder,
} from "../../actions/studensts";

const fmtDateTime = (val) => {
  if (!val) return "";
  const d = new Date(String(val).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function RushReminderModal({ onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [info, setInfo] = useState({ after_days: 15, send_hour: 9 });
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [sendingNow, setSendingNow] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getRushReminderRecipients();
    if (res?.status === 200) {
      const { data, ...rest } = res.data;
      setRecipients(data || []);
      setInfo(rest);
    } else {
      setError("Failed to load the reminder list. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clearMessages = () => {
    setError("");
    setNotice("");
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;
    setSaving(true);
    clearMessages();
    const res = await addRushReminderRecipient(email);
    setSaving(false);
    if (res?.status === 201) {
      setNewEmail("");
      load();
    } else {
      setError(res?.data?.ERROR || "Could not add that email. Please try again.");
    }
  };

  const handleRemove = async (id) => {
    setRemovingId(id);
    clearMessages();
    const res = await deleteRushReminderRecipient(id);
    setRemovingId(null);
    if (res?.status === 200) {
      setRecipients((prev) => prev.filter((r) => r.id !== id));
    } else {
      setError(res?.data?.ERROR || "Could not remove that email. Please try again.");
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    clearMessages();
    const res = await runRushReminder();
    setSendingNow(false);
    if (res?.status === 200) {
      const { students = 0, sent = 0, message } = res.data || {};
      if (students > 0) {
        setNotice(`Reminder sent to ${sent} recipient(s) covering ${students} rush student(s).`);
        load();
      } else {
        setNotice(message || "Nothing to send right now.");
      }
    } else {
      setError(res?.data?.ERROR || "Sending the reminder failed. Please try again.");
    }
  };

  const busy = saving || sendingNow;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-60"
        onClick={() => !busy && onClose()}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl p-7 w-[30rem] max-w-[calc(100%-2rem)] z-10">
        <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-3">
          Rush Reminder Emails
        </h2>
        <p className="text-xs text-gray-500 mb-2">
          Once a day (from {String(info.send_hour).padStart(2, "0")}:00), a reminder
          listing every rush student who has passed {info.after_days} days since
          authentication and is still not delivered is mailed to these addresses.
          A student stops appearing once their state is MOHE Validation, MOHE, or
          Done.
        </p>
        <p className="text-xs text-gray-500 mb-4">
          {info.last_sent_at
            ? `Last sent ${fmtDateTime(info.last_sent_at)} (${info.last_student_count} student${
                info.last_student_count === 1 ? "" : "s"
              }).`
            : "No reminder has been sent yet."}
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
        )}
        {notice && (
          <div className="mb-3 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
            {notice}
          </div>
        )}

        <form onSubmit={handleAdd} className="flex gap-2 mb-4">
          <input
            type="email"
            placeholder="name@americanacademyeg.com"
            className="flex-1 border border-gray-300 rounded-lg p-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={saving}
          />
          <button
            type="submit"
            disabled={saving || !newEmail.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                       disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
          >
            {saving ? "Adding..." : "Add"}
          </button>
        </form>

        <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-5">
          {loading ? (
            <div className="py-6 text-center text-gray-500 text-sm">Loading...</div>
          ) : recipients.length === 0 ? (
            <div className="py-6 text-center text-gray-500 text-sm">
              No addresses yet — no reminders will be sent until you add one.
            </div>
          ) : (
            recipients.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-3 py-2">
                <div>
                  <div className="text-sm text-gray-800">{r.email}</div>
                  {r.created_by && (
                    <div className="text-xs text-gray-400">added by {r.created_by}</div>
                  )}
                </div>
                <button
                  onClick={() => handleRemove(r.id)}
                  disabled={removingId === r.id}
                  className="text-red-600 hover:text-red-800 text-xs font-semibold disabled:opacity-50"
                >
                  {removingId === r.id ? "Removing..." : "Remove"}
                </button>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-between items-center gap-3">
          <button
            onClick={handleSendNow}
            disabled={sendingNow || recipients.length === 0}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700
                       disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
            title="Send today's reminder now instead of waiting for the scheduled time"
          >
            {sendingNow ? "Sending..." : "Send Now"}
          </button>
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
